import CheckmarkIcon from '@assets/icons/checkmark-thin.svg';
import DeleteIcon from '@assets/icons/delete.svg';
import i18n from '@/utils/i18n';
import React, { useCallback } from 'react';
import { APP_MODAL_ROUTES, ORDER_INBOUNDS_ROUTES } from '@/navigators/routes';
import {
  AppModalStackParams,
  OrderInboundsStackParams,
} from '@/navigators/types';
import { Button } from '@/components/button';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { createStyles } from '@/contexts/theme-context';
import { Linking, ScrollView, View } from 'react-native';
import { ModalContainer } from '@/components/modal-container';
import { StackScreenProps } from '@react-navigation/stack';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTabCardImage } from '@/hooks/payments/use-tab-card-image';
import { useToasts } from '@/contexts/toast-context';

// Registered in both the app modal stack and the order agent stack, which
// cannot reach each other.
type Props =
  | StackScreenProps<AppModalStackParams, APP_MODAL_ROUTES.TabCardApplication>
  | StackScreenProps<
      OrderInboundsStackParams,
      ORDER_INBOUNDS_ROUTES.TabCardApplication
    >;

export const TabCardApplicationScreen: React.FC<Props> = ({ route }) => {
  const { cashbackPercentage, distributorName, imageUrl, signUpUrl } =
    route.params;
  const { source, onImageLoadError } = useTabCardImage(imageUrl);
  const { bottom } = useSafeAreaInsets();
  const { showErrorToast } = useToasts();
  const [styles, theme] = useStyles();

  const handleSignUp = useCallback(async () => {
    try {
      await Linking.openURL(signUpUrl);
    } catch (e) {
      showErrorToast();
    }
  }, [signUpUrl, showErrorToast]);

  const cashBackCopy =
    cashbackPercentage > 0
      ? [
          i18n.t('tab_card_modal.cash_back_on_purchases', {
            cashbackPercentage: String(cashbackPercentage),
            distributorName,
          }),
          i18n.t('tab_card_modal.cash_back_everywhere'),
        ]
      : [
          i18n.t('tab_card_modal.cash_back_on_select_products', {
            distributorName,
          }),
        ];

  const benefitsCopy = [
    ...cashBackCopy,
    i18n.t('tab_card_modal.expense_management_portal'),
  ];

  return (
    <ModalContainer
      navigationHeaderProps={{
        BackButtonIcon: DeleteIcon,
        backButtonTestID: 'tab-card-application-close-button',
        centerTitle: true,
      }}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.container}>
          <Typography variant="heading-lg">
            {i18n.t('tab_card_modal.header')}
          </Typography>
          <View style={styles.cardContainer}>
            <CachedImage
              accessibilityIgnoresInvertColors
              onError={onImageLoadError}
              source={source}
              style={styles.cardImage}
            />
          </View>
          <View style={styles.textContent}>
            <Typography variant="paragraph-base">
              {i18n.t('tab_card_modal.loyalty_program_intro', {
                distributorName,
              })}
            </Typography>
            <View style={styles.benefitsList}>
              {benefitsCopy.map((text) => (
                <View key={text} style={styles.benefitItem}>
                  <CheckmarkIcon
                    height={16}
                    stroke={theme.sk.color.icon.primary}
                    width={16}
                  />
                  <View style={styles.benefitText}>
                    <Typography variant="paragraph-base">{text}</Typography>
                  </View>
                </View>
              ))}
            </View>
            <Typography variant="paragraph-base">
              {i18n.t('tab_card_modal.apply_for_free')}
            </Typography>
          </View>
        </View>
      </ScrollView>
      <Button
        onPress={handleSignUp}
        style={[styles.setupButton, { marginBottom: Math.max(bottom, 24) }]}
        trackingData={{ name: 'tab_card_application.sign_up_button' }}
      >
        {i18n.t('tab_card_modal.set_up_your_account')}
      </Button>
    </ModalContainer>
  );
};

const useStyles = createStyles((theme) => ({
  benefitItem: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  benefitsList: {
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
  },
  benefitText: {
    flex: 1,
  },
  cardContainer: {
    alignItems: 'center',
  },
  cardImage: {
    height: 206,
    maxWidth: '100%',
    width: 327,
  },
  container: {
    gap: theme.sk.spacing.spaceLarge,
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: theme.sk.spacing.spaceLarge,
  },
  setupButton: {
    borderRadius: theme.sk.radii.radiusMedium,
    marginHorizontal: theme.sk.spacing.spaceMedium,
    marginTop: theme.sk.spacing.spaceSmall,
  },
  textContent: {
    gap: theme.sk.spacing.spaceMedium,
    marginBottom: theme.sk.spacing.spaceMedium,
  },
}));
